import { Heading } from '@chakra-ui/react';
import { IQuoteRespSchema } from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { FC } from 'react';
import { useNavigate } from 'react-router';
import { useDealRoomConfig } from '../../../../../../api/settingsService';
import {
  MButton,
  MText,
  MTooltip,
} from '../../../../../../components/Monetize';
import { MTooltipContentList } from '../../../../../../components/Monetize/MTooltipContentList';
import { getDealRoomByDealRoom } from '../../../../../../constants/routes';
import { useExperienceConfiguration } from '../../../../../../hooks';
import { useFlags } from '../../../../../../services/launchDarkly';
import { getEsignContactValidity } from '../../../../Quote/components/contacts/quoteContactUtils';
import { useQuoteContext } from '../../../../Quote/quoteContext';
import { QuoteShareSendPopover } from './QuoteShareSendPopover';
import { useQuoteSendModalActions } from './useQuoteSendModalActions';

interface QuoteSendButtonProps {
  isActionLoading: boolean;
  isEsignEnabled: boolean;
  quote: IQuoteRespSchema;
  setQuote: (quote: IQuoteRespSchema | null) => void;
  onSendQuote: () => void;
}

export const QuoteSendButton: FC<QuoteSendButtonProps> = ({
  isActionLoading,
  isEsignEnabled,
  quote,
  setQuote,
  onSendQuote,
}: QuoteSendButtonProps) => {
  const navigate = useNavigate();
  const { useDealRoom } = useFlags();
  const { quoteShareConfig } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const { data: dealRoomConfig } = useDealRoomConfig();
  const {
    quoteSettingsData: { quoteSettings },
  } = useQuoteContext();
  const { allowEsignEditOnQuoteReview } = quoteSettings || {};

  const { isDocumentsLoading, onClickSend } = useQuoteSendModalActions({
    isEsignEnabled,
    quote,
    onSendQuote,
  });

  const { isValid: isEsignSetupValid, errorMsgs: eSignContactsErrorMsgs } =
    getEsignContactValidity(quote);

  const isDisabledDueToEsign =
    allowEsignEditOnQuoteReview && !isEsignSetupValid;

  const isEsignSend = quote.requiresEsign && isEsignEnabled;
  /**
   * Admins can require every eSign quote to go through Quote Share, so the rep
   * no longer gets a direct "Send via eSign" action. The customer starts eSign
   * by accepting through the share link.
   */
  const isManualEsignSendDisabled =
    !!quoteShareConfig?.enabled && !!quoteShareConfig?.esign?.disableManualSend;

  let disabledSubmitMessages: string[] = [];
  if (isDisabledDueToEsign) {
    if (!isEsignSetupValid) {
      disabledSubmitMessages = disabledSubmitMessages.concat(
        eSignContactsErrorMsgs,
      );
    }
  }

  const sendTooltipContent = (
    <MTooltipContentList
      title="To advance, resolve required actions below"
      listItems={disabledSubmitMessages}
    />
  );

  /**
   * If deal room exists and is enabled, direct user to the deal room
   */
  const dealRoomId = quote.opportunity?.dealRoomId;
  if (dealRoomId) {
    return (
      <MButton
        mr={1.5}
        variant="primary"
        data-testid="quote-send-btn"
        isLoading={isActionLoading}
        onClick={() => navigate(getDealRoomByDealRoom(dealRoomId))}
      >
        Continue in Deal Room
      </MButton>
    );
  }

  /**
   * If deal room or quote share are enabled, show the share button popover experience
   */
  if ((useDealRoom && dealRoomConfig?.enabled) || quoteShareConfig?.enabled) {
    return (
      <MTooltip
        shouldWrapChildren
        isDisabled={!isDisabledDueToEsign}
        bg="tPurple.base"
        label={sendTooltipContent}
      >
        <QuoteShareSendPopover
          isActionLoading={isActionLoading}
          isDisabled={isDisabledDueToEsign}
          quote={quote}
          setQuote={setQuote}
        >
          {({ isCreateDealRoomLoading }) => {
            if (isEsignSend) {
              if (isManualEsignSendDisabled) {
                return null;
              }
              return (
                <>
                  <Heading as="h4" fontSize="md" mb={2}>
                    Send via eSign
                  </Heading>
                  <MText mb={2}>
                    Use this option to send the quote via eSign.
                  </MText>
                  <MButton
                    mr={1.5}
                    variant="primary"
                    data-testid="quote-send-btn"
                    width="100%"
                    isLoading={isActionLoading || isDocumentsLoading}
                    isDisabled={isActionLoading || isCreateDealRoomLoading}
                    onClick={onClickSend}
                  >
                    Send via eSign
                  </MButton>
                </>
              );
            }
            return (
              <>
                <Heading as="h4" fontSize="md" mb={2}>
                  Manually Progress Quote
                </Heading>
                <MText mb={2}>
                  Use this option to progress the quote manually without using
                  Deal Room or Quote Share.
                </MText>
                <MButton
                  mr={1.5}
                  variant="secondary"
                  data-testid="quote-send-btn"
                  width="100%"
                  isLoading={isActionLoading}
                  isDisabled={isActionLoading || isCreateDealRoomLoading}
                  onClick={onClickSend}
                >
                  Mark as Sent
                </MButton>
              </>
            );
          }}
        </QuoteShareSendPopover>
      </MTooltip>
    );
  }

  /**
   * Show send with esign button
   */
  if (isEsignSend) {
    return (
      <MTooltip
        shouldWrapChildren
        isDisabled={!isDisabledDueToEsign}
        bg="tPurple.base"
        label={sendTooltipContent}
      >
        <MButton
          mr={1.5}
          variant="primary"
          data-testid="quote-send-btn"
          isLoading={isActionLoading || isDocumentsLoading}
          isDisabled={isActionLoading || isDisabledDueToEsign}
          onClick={onClickSend}
        >
          Send via eSign
        </MButton>
      </MTooltip>
    );
  }

  /**
   * Show manual send button
   */
  return (
    <MButton
      mr={1.5}
      variant="primary"
      data-testid="quote-send-btn"
      isLoading={isActionLoading}
      isDisabled={isActionLoading}
      onClick={onClickSend}
    >
      Mark as Sent
    </MButton>
  );
};
